import { useState } from 'react';

import { useModalControl, ModalControlResponse } from '@weave/design-system';
import { useAlert } from '@weave/design-system';

import { fetchAutoReminderRules, DocumentType, Rules } from 'shared/helpers/axios/apis';

import AutoReminderWarningModal from './auto-reminder-warning-modal/auto-reminder-warning-modal.component';
import {
  ActiveReminder,
  AutoReminderCheckResult,
  ReminderType,
} from './auto-reminder.types';

interface AutoReminderCheckParams {
  type: DocumentType;
  entityId: string;
}

interface UseAutoReminderCheckResults extends ModalControlResponse {
  AutoReminderWarningModal: typeof AutoReminderWarningModal;
  checkIfPartOfAutoReminder: (
    params: AutoReminderCheckParams
  ) => Promise<{ canProceed: boolean }>;
  autoReminders: AutoReminderCheckResult;
}

enum templateType {
  Individual = ' (Individual)',
  Family = ' (Family)',
  IndividualSpanish = ' (Individual - Spanish)',
  FamilySpanish = ' (Family - Spanish)',
}

export const useAutoReminderCheck = (): UseAutoReminderCheckResults => {
  const alert = useAlert();
  const modalControlProps = useModalControl();

  const [autoReminders, setAutoReminders] = useState<AutoReminderCheckResult>({
    success: false,
    hasActiveReminders: false,
    appointmentReminders: [],
    appointmentFollowup: [],
    birthdays: [],
    recall: [],
    reviews: [],
    saveTheDates: [],
    missedCalls: [],
    orderReady: [],
    unspecified: [],
  });

  async function checkIfPartOfAutoReminder(
    params: AutoReminderCheckParams
  ): Promise<{ canProceed: boolean }> {
    try {
      const {
        data: { rules },
      } = await fetchAutoReminderRules();

      const results = processAutoRemindersRules(rules, params);
      setAutoReminders(results);

      if (results.hasActiveReminders) {
        modalControlProps.openModal();
        return { canProceed: false };
      }

      return { canProceed: true };
    } catch (err) {
      alert.error('Failed to check auto reminders!');

      setAutoReminders({
        success: false,
        hasActiveReminders: false,
        appointmentReminders: [],
        appointmentFollowup: [],
        birthdays: [],
        recall: [],
        reviews: [],
        saveTheDates: [],
        missedCalls: [],
        orderReady: [],
        unspecified: [],
      });

      return { canProceed: false };
    }
  }

  return {
    AutoReminderWarningModal,
    ...modalControlProps,
    checkIfPartOfAutoReminder,
    autoReminders,
  };
};

function processAutoRemindersRules(
  rules: Rules[],
  entity: AutoReminderCheckParams
): AutoReminderCheckResult {
  const result: AutoReminderCheckResult = {
    success: false,
    hasActiveReminders: false,
    appointmentReminders: [],
    appointmentFollowup: [],
    birthdays: [],
    recall: [],
    reviews: [],
    saveTheDates: [],
    missedCalls: [],
    orderReady: [],
    unspecified: [],
  };

  for (const rule of rules) {
    for (const template of rule.templates) {
      if (
        template.form &&
        template.form.documentType === entity.type &&
        template.form.documentId === entity.entityId
      ) {
        const suffix = getRuleNameSuffix(template.locale, template.personStrategy);
        pushTypesToResult(
          rule.autoMessageType.slug,
          rule.id + suffix,
          rule.name + suffix
        );
      }
    }

    if (
      result.appointmentReminders.length ||
      result.appointmentFollowup.length ||
      result.birthdays.length ||
      result.recall.length ||
      result.reviews.length ||
      result.saveTheDates.length ||
      result.missedCalls.length ||
      result.orderReady.length ||
      result.unspecified.length
    ) {
      result.hasActiveReminders = true;
    }
  }

  function getRuleNameSuffix(locale: string, personStrategy: string) {
    switch (locale) {
      case 'en_US':
        if (personStrategy === 'DEFAULT') {
          return templateType.Individual;
        }
        return templateType.Family;
      case 'es_ES':
        if (personStrategy === 'DEFAULT') {
          return templateType.IndividualSpanish;
        }
        return templateType.FamilySpanish;
    }
    return '';
  }

  function pushTypesToResult(appointmentType: string, ruleId: string, ruleName: string) {
    switch (appointmentType) {
      case ReminderType.AppointmentReminder:
        result.appointmentReminders.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.AppointmentFollowup:
        result.appointmentFollowup.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.BirthdayReminder:
        result.birthdays.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.RecallReminder:
        result.recall.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.ReviewReminder:
        result.reviews.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.SaveTheDateReminder:
        result.saveTheDates.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.MissedCall:
        result.missedCalls.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      case ReminderType.OrderReady:
        result.orderReady.push({
          id: ruleId,
          name: ruleName,
        });
        break;
      default:
        result.unspecified.push({
          id: ruleId,
          name: ruleName,
        });
        break;
    }
  }

  return result;
}
